import React, { useState, useMemo } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Layout, PensieveHeader, PensievePagination, PensievePosts } from '@components';
import 'katex/dist/katex.min.css';

const StyledMainContainer = styled.main`
  & > header {
    margin-bottom: 100px;
    text-align: center;

    a {
      &:hover,
      &:focus {
        cursor: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='48' viewport='0 0 100 100' style='fill:black;font-size:24px;'><text y='50%'>🤖</text></svg>")
            20 0,
          auto;
      }
    }
  }

  footer {
    ${({ theme }) => theme.mixins.flexBetween};
    width: 100%;
    margin-top: 20px;
  }
`;

const PensievePage = ({ location, data }) => {
  const allPosts = data.allMarkdownRemark.edges;

  const [selectedTag, setSelectedTag] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredPosts = useMemo(() => {
    let tempPosts = allPosts;

    if (selectedTag) {
      tempPosts = tempPosts.filter(({ node }) =>
        node.frontmatter.tags.includes(selectedTag),
      );
    }

    if (searchTerm.trim() !== '') {
      const lowercasedSearchTerm = searchTerm.toLowerCase().trim();
      tempPosts = tempPosts.filter(({ node }) => {
        const { title, tags } = node.frontmatter;

        const titleMatch = title.toLowerCase().includes(lowercasedSearchTerm);
        const tagMatch = tags.some(tag => tag.toLowerCase().includes(lowercasedSearchTerm));

        return titleMatch || tagMatch;
      });
    }

    return tempPosts;
  }, [allPosts, selectedTag, searchTerm]);

  return (
    <Layout location={location}>
      <Helmet title="Pensieve" />

      <StyledMainContainer>
        <PensieveHeader
          data={data}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setSelectedTag={setSelectedTag}
          setCurrentPage={setCurrentPage}
        />

        <PensievePosts
          currentPage={currentPage}
          filteredPosts={filteredPosts}
        />

        <PensievePagination
          filteredPosts={filteredPosts}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </StyledMainContainer>
    </Layout>
  );
};

PensievePage.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
};

export default PensievePage;

export const pageQuery = graphql`
  {
    allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/content/posts/" }, frontmatter: { draft: { ne: true }, slug: { ne: null } } }
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      edges {
        node {
          frontmatter {
            title
            description
            slug
            date
            tags
            draft
          }
          html
        }
      }
    }
  }
`;
